import React from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { NERResult, SynonymResult, SynonymTag } from "@/model";


interface EntityAnalysisCardProps {
    nerResult: NERResult,
    synonymResult: SynonymResult
}

const EntityAnalysisCard: React.FC<EntityAnalysisCardProps> = (props) => {
    const { nerResult, synonymResult } = props;

    const intl = useIntl();

    return (
        <div className="single-test-card gap-y-4">
            <div className="flex flex-col gap-1">
                <div className="single-test-card-subhead">
                    {intl.formatMessage({ id: "i00006" })}
                </div>
                {partEntity(nerResult.tagged_queries)
                    .map(({ text, type }, index) =>
                        <div key={index}
                             className={classNames(
                                 "h-[23px] inline-flex items-center font-[400] text-[13px] leading-none",
                                 getTextClassName(type)
                             )}
                        >
                            {text}
                        </div>
                    )
                }
            </div>

            <div className="flex flex-col gap-1">
                <div className="single-test-card-subhead">
                    {intl.formatMessage({ id: "i00007" })}
                </div>

                <div className="flex flex-wrap gap-x-0.5 gap-y-1">
                    {partSynonym(synonymResult.query, synonymResult.synonyms)
                        .map(({ text, type }, index) =>
                            <div key={index}
                                 className={classNames(
                                     "h-[23px] inline-flex items-center font-[400] text-[13px] leading-none",
                                     getTextClassName(type)
                                 )}
                            >
                                {text}
                            </div>
                        )
                    }
                </div>
            </div>

            {nerResult.entities.length > 0 && (
                <div className="flex flex-col gap-1">
                    <div className="single-test-card-subhead">
                        {intl.formatMessage({ id: "i00008" })}
                    </div>

                    <div className="flex flex-col gap-1">
                        {nerResult.entities.map((entity, index) => (
                            <div key={index}
                                 className="flex justify-between items-center p-[8px_10px] rounded-[6px] border border-black-200"
                            >
                                <div className="font-[400] text-[13px] leading-[1.5] text-black-500">
                                    @{entity.name}
                                </div>
                                <div className="font-[400] text-[13px] leading-[1.5] text-black-500">
                                    {entity.value}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

type HighLightPart = {
    text: string,
    type?: "synonym" | "entity"
}

const getTextClassName = (type: "synonym" | "entity" | undefined) => {
    if (type === "entity") {
        return "bg-chat42-10 rounded-[10px] px-2 text-chat42";
    }
    if (type === "synonym") {
        return "bg-point-red-20 rounded-[10px] px-2 text-point-red";
    }
    return "text-black-400";
};

const partEntity = (text: string) => {
    const regex = /\{(.*?)}/g;
    const parts: HighLightPart[] = [];
    let lastIndex = 0;

    let match;
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push({ text: text.slice(lastIndex, match.index) });
        }
        parts.push({
            text: match[1],
            type: "entity"
        });
        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push({ text: text.slice(lastIndex) });
    }
    return parts;
};

const partSynonym = (query: string, synonymTags?: SynonymTag[]) => {
    if (!synonymTags?.length) {
        return partEntity(query);
    }
    const parts: HighLightPart[] = [];

    synonymTags.forEach((synonym, index) => {
        const lastIndex = index ? synonymTags[index - 1].end_idx : -1;

        if (lastIndex + 1 < synonym.start_idx) {
            parts.push(...partEntity(query.slice(lastIndex + 1, synonym.start_idx)));
        }
        parts.push({ text: synonym.keyword, type: "synonym" });

        if (index === synonymTags.length - 1 && synonym.end_idx + 1 < query.length) {
            parts.push(...partEntity(query.slice(synonym.end_idx + 1)));
        }
    });
    return parts;
};

export default EntityAnalysisCard;
